import { useEffect, useState } from 'react';
import { Gift, CalendarClock, Loader2, Check, AlertTriangle, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const monthLabel = (m, isAr) => {
  if (!isAr) return `${m} ${m === 1 ? 'month' : 'months'}`;
  if (m === 1) return 'شهر واحد';
  if (m === 2) return 'شهرين';
  if (m <= 10) return `${m} أشهر`;
  return `${m} شهراً`;
};

export const tierLabel = (t, isAr, currency = 'AED') => `${monthLabel(t.months, isAr)} — ${t.price_aed} ${currency}`;

/**
 * AdPlanPicker — free quota first (2 free ads / category / 30 days), then paid tiers
 * read live from the admin platform (months → total price in AED). No local prices.
 *
 * onChange({ isFree, tierId, months, fee, currency, ready })
 */
export default function AdPlanPicker({ category, isAr, isDarkMode, value, onChange }) {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${BACKEND_URL}/api/user/ads/pricing`, { headers: getAuthHeaders() })
      .then((r) => { if (alive) setQuota({ currency: r.data.currency, ...r.data.categories?.[category] }); })
      .catch(() => { if (alive) setQuota(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [category]);

  useEffect(() => {
    if (!quota) return;
    if (quota.next_is_free) {
      onChange?.({ isFree: true, tierId: null, months: null, fee: 0, currency: quota.currency, ready: true });
    } else if (!value?.tierId) {
      onChange?.({ isFree: false, tierId: null, months: null, fee: null, currency: quota.currency, ready: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quota]);

  const frame = isDarkMode ? 'bg-[#121520] border border-[#1E9BD9]/20' : 'bg-white border border-[#1E9BD9]/25';
  const muted = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const currency = quota?.currency || 'AED';

  if (loading) {
    return (
      <div className={`rounded-2xl p-4 flex items-center gap-2 text-sm ${frame} ${muted}`} data-testid="ad-plan-loading">
        <Loader2 className="h-4 w-4 animate-spin text-[#1E9BD9]" />
        {isAr ? 'جاري التحقق من باقة الإعلان…' : 'Checking your ad plan…'}
      </div>
    );
  }
  if (!quota) return null;

  if (quota.next_is_free) {
    return (
      <div className={`rounded-2xl p-4 flex items-center gap-3 ${frame}`} data-testid="ad-plan-free">
        <span className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center shrink-0">
          <Gift className="h-5 w-5 text-emerald-500" />
        </span>
        <div className="text-sm">
          <p className="font-bold text-emerald-500">{isAr ? 'هذا الإعلان مجاني 🎉' : 'This ad is free 🎉'}</p>
          <p className={muted}>
            {isAr
              ? `متبقي لك ${quota.free_remaining} من ${quota.free_ads} إعلانات مجانية في هذه الفئة · مدة الإعلان ${quota.free_duration_days} يوماً`
              : `${quota.free_remaining} of ${quota.free_ads} free ads left in this category · runs for ${quota.free_duration_days} days`}
          </p>
        </div>
      </div>
    );
  }

  if (!quota.paid_available) {
    return (
      <div className={`rounded-2xl p-4 flex items-center gap-3 ${frame}`} data-testid="ad-plan-unavailable">
        <span className="w-10 h-10 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center shrink-0">
          <AlertTriangle className="h-5 w-5 text-[#F59E0B]" />
        </span>
        <div className="text-sm">
          <p className="font-bold text-[#F59E0B]">{isAr ? 'النشر المدفوع غير متاح حالياً' : 'Paid publishing is currently unavailable'}</p>
          <p className={muted}>
            {isAr
              ? `استخدمت إعلاناتك المجانية (${quota.free_ads}) في هذه الفئة، ولم تُحدَّد باقات مدفوعة بعد. حاول لاحقاً.`
              : `You used your ${quota.free_ads} free ads in this category and no paid plans are configured yet. Please try later.`}
          </p>
        </div>
      </div>
    );
  }

  const selected = quota.tiers.find((t) => t.id === value?.tierId);

  return (
    <div className={`rounded-2xl p-4 ${frame}`} data-testid="ad-plan-paid">
      <div className="flex items-center gap-2 mb-3">
        <CalendarClock className="h-5 w-5 text-[#F59E0B]" />
        <div className="text-sm">
          <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{isAr ? 'اختر مدة الإعلان' : 'Choose ad duration'}</p>
          <p className={muted}>
            {isAr
              ? `استخدمت إعلاناتك المجانية (${quota.free_ads}) في هذه الفئة — الإعلانات الإضافية برسوم حسب المدة.`
              : `You used your ${quota.free_ads} free ads in this category — extra ads are paid by duration.`}
          </p>
        </div>
      </div>

      <div className="relative">
        <select
          value={value?.tierId || ''}
          onChange={(e) => {
            const t = quota.tiers.find((x) => x.id === e.target.value);
            onChange?.(t
              ? { isFree: false, tierId: t.id, months: t.months, fee: t.price_aed, currency, ready: true }
              : { isFree: false, tierId: null, months: null, fee: null, currency, ready: false });
          }}
          className={`w-full h-12 appearance-none rounded-xl px-4 pe-10 text-sm font-semibold border-2 outline-none transition-colors focus:border-[#1E9BD9] ${isDarkMode ? 'bg-[#0A0B10] border-[#1E9BD9]/25 text-white' : 'bg-white border-[#1E9BD9]/30 text-slate-800'}`}
          data-testid="ad-plan-tier-select"
        >
          <option value="">{isAr ? 'اختر المدة والسعر…' : 'Select duration & price…'}</option>
          {quota.tiers.map((t) => (
            <option key={t.id} value={t.id} data-testid={`ad-plan-tier-${t.id}`}>{tierLabel(t, isAr, currency)}</option>
          ))}
        </select>
        <ChevronDown className={`pointer-events-none absolute top-1/2 -translate-y-1/2 end-3 h-4 w-4 ${muted}`} />
      </div>

      {selected && (
        <div className="mt-3 flex items-center justify-between rounded-xl px-3 py-2 bg-gradient-to-r from-[#1E9BD9]/10 to-[#F5A962]/10 border border-[#1E9BD9]/20" data-testid="ad-plan-summary">
          <span className={`text-xs font-semibold inline-flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            <Check className="h-3.5 w-3.5 text-emerald-500" />
            {monthLabel(selected.months, isAr)}
          </span>
          <span className="text-sm font-black text-[#F59E0B] tabular-nums" dir="ltr">{selected.price_aed} {currency}</span>
        </div>
      )}

      <p className={`text-[11px] mt-2 ${muted}`}>
        {isAr ? 'السعر إجمالي للمدة كاملة. سيتم تحويلك لإتمام الدفع بعد الحفظ، ويُفعَّل الإعلان فور تأكيد الدفع.' : 'Price is the total for the whole period. You will be redirected to pay after saving; the ad activates once payment is confirmed.'}
      </p>
    </div>
  );
}
